import { z } from "zod";
//#region src/oauth-consent-view.ts
/**
* The consent view of the CP authorization server (plan 2026-10-28-identity-mcp-tools, R-020).
*
* `GET /v1/auth/oauth2/authorize/:id` of cp-auth-api answers this view. The consent page of
* cp-admin-ui (`/oauth/consent`) renders it, then posts approve or deny. The DP consent view is a
* different shape: `authorizeRequestViewSchema` of `oauth.ts` requires `orgName`, which the CP does
* not have. This module holds the CP shape alone.
*
* Who reads it:
* - cp-auth-api (WS-7): `OAuthAsService.getAuthorizeView` writes the view;
* - cp-admin-ui (WS-8): the consent page reads the view and offers the passkey elevation when
*   {@link CpAuthorizeRequestView.elevationRequired} holds an entry (R-020 item 4).
*
* Browser-safe: no Node builtin and no Node global. No schema uses `.transform()`, so each one stays
* representable in the JSON Schema that `createOpenApiDocument()` writes.
*/
/**
* One requested scope of a pending authorization request, as the consent page shows it.
*
* `slug` is the CP permission string. `held` tells whether the active role of the session that
* read the view holds the permission, from the live database read of cp-auth-api, never from the
* claims of the token. The CP has no labelled permission catalog, so the view carries no label,
* description or group.
*/
const cpConsentScopeViewSchema = z.object({
	slug: z.string().min(1),
	held: z.boolean()
}).strict();
/**
* The consent view of one pending CP authorization request.
*
* Fields:
* - `id`: the request id, a UUID, from the `request` query parameter of the consent page.
* - `clientId`: the client id. For a `dynamic` client it is text that the client chose.
* - `clientName`: 1 to 200 characters, the bound of both writers (the admin client body and
*   the dynamic registration body of cp-auth-api). For a `dynamic` client it is text that the client
*   chose. The page renders it as inert text.
* - `redirectHost`: the display form of the redirect URI (`consentRedirectDisplay` of
*   cp-auth-api). The page renders it at the same weight as `clientName`.
* - `registrationType`: `admin` or `dynamic`. A `dynamic` client shows the unverified marker.
* - `scopes`: the scopes that an approve grants now, each with `held: true`. When the active role
*   holds none of the requested scopes, every requested scope with `held: false`. One or more
*   entries, no slug twice.
* - `notGranted`: the requested scopes that the active role does not hold, which an approve does
*   not grant. Empty when the active role holds every requested scope, or none of them.
* - `elevationRequired`: see the field.
*
* Refusals. A parse fails:
* - on an unknown key, at the top level or in a scope (`.strict()`);
* - when `scopes` mixes `held: true` and `held: false`;
* - when no scope is held and `notGranted` holds an entry;
* - when a slug repeats in `scopes`, or a `notGranted` entry is also a slug of `scopes`;
* - when an `elevationRequired` entry repeats, or is not a requested scope (a slug of `scopes` or
*   an entry of `notGranted`).
*
* The parse has no side effect and returns the input unchanged on success.
*/
const cpAuthorizeRequestViewSchema = z.object({
	id: z.uuid(),
	clientId: z.string().min(1),
	clientName: z.string().min(1).max(200),
	redirectHost: z.string().min(1),
	registrationType: z.enum(["admin", "dynamic"]),
	scopes: z.array(cpConsentScopeViewSchema).min(1),
	notGranted: z.array(z.string().min(1)),
	/**
	* The requested elevated-only scopes that an approve from this session does not grant
	* (R-020 items 2 and 4).
	*
	* An elevated-only scope is an entry of `CP_ELEVATED_ONLY_SCOPES` of cp-auth-api. An approve
	* grants one only when the approving session acts as `PLATFORM_ADMIN_ROLE` and holds a live
	* elevation (`elevatedUntil` later than the approve). cp-auth-api fills the field from the
	* request and from the session that read the view:
	* 1. the request names no elevated-only scope: empty;
	* 2. the session acts as `PLATFORM_ADMIN_ROLE` with a live elevation: empty;
	* 3. any other case: every requested elevated-only scope, in the order of the request.
	*
	* Each entry is also a slug of `scopes` or an entry of `notGranted`, and no entry repeats.
	*
	* The consent page offers the passkey elevation (the `role-elevation` step-up) when the field
	* holds an entry, then reads the view again. An approve without the elevation grants the
	* delegable part alone. The approve checks the elevation again, so the field is a hint to
	* the page and never the authority.
	*
	* Absent from a cp-auth-api built before S4 of the plan. A reader takes absence as empty: such
	* a server refuses an elevated-only scope at authorize, so its view never names one.
	*/
	elevationRequired: z.array(z.string().min(1)).optional()
}).strict().superRefine((view, ctx) => {
	const slugs = view.scopes.map((s) => s.slug);
	const heldCount = view.scopes.filter((s) => s.held).length;
	if (heldCount !== 0 && heldCount !== view.scopes.length) ctx.addIssue({
		code: "custom",
		path: ["scopes"],
		message: "scopes mixes held and not held entries"
	});
	if (heldCount === 0 && view.notGranted.length > 0) ctx.addIssue({
		code: "custom",
		path: ["notGranted"],
		message: "notGranted holds an entry while no scope is held"
	});
	if (new Set(slugs).size !== slugs.length) ctx.addIssue({
		code: "custom",
		path: ["scopes"],
		message: "a slug repeats in scopes"
	});
	if (view.notGranted.some((slug) => slugs.includes(slug))) ctx.addIssue({
		code: "custom",
		path: ["notGranted"],
		message: "a notGranted entry is also a slug of scopes"
	});
	const elevationRequired = view.elevationRequired ?? [];
	if (new Set(elevationRequired).size !== elevationRequired.length) ctx.addIssue({
		code: "custom",
		path: ["elevationRequired"],
		message: "an elevationRequired entry repeats"
	});
	const requested = [...slugs, ...view.notGranted];
	if (elevationRequired.some((slug) => !requested.includes(slug))) ctx.addIssue({
		code: "custom",
		path: ["elevationRequired"],
		message: "an elevationRequired entry is not a requested scope"
	});
});
//#endregion
export { cpAuthorizeRequestViewSchema, cpConsentScopeViewSchema };

//# sourceMappingURL=oauth-consent-view.js.map